// src/pages/VerifyEmail.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAlert } from "../components/CustomAlert";
import "../styles/Auth.css";
import {
  getAuth,
  applyActionCode,
  checkActionCode,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";

function useQuery() {
  const { search } = useLocation();
  return useMemo(function () {
    return new URLSearchParams(search);
  }, [search]);
}

function VerifyEmail() {
  const auth = getAuth();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation(["verify", "auth", "actions"]);
  const tv = function (k, opt) {
    return t(k, Object.assign({ ns: "verify" }, opt));
  };
  const ta = function (k, opt) {
    return t(k, Object.assign({ ns: "auth" }, opt));
  };
  const tk = function (k, opt) {
    return t(k, Object.assign({ ns: "actions" }, opt));
  };

  const { showAlert } = useAlert();
  const query = useQuery();

  const [status, setStatus] = useState("processing");
  const [processingMsg, setProcessingMsg] = useState("");
  const lastHandledOobRef = useRef(null);

  const clearUrlParams = function () {
    try {
      if (window && window.history && window.history.replaceState) {
        var clean = new URL("/verify-email", window.location.origin);
        window.history.replaceState(null, "", clean.pathname);
      }
    } catch (e) {}
  };

  const resendVerification = async function () {
    var user = auth.currentUser;
    if (!user) {
      showAlert(
        tv("errorTitle") || "提示",
        tv("errorMessage") || "请先登录后再试。",
        tk("ok") || "我知道了",
        "error",
        function () {
          navigate("/login");
        }
      );
      return;
    }

    try {
      await user.reload();
      if (user.emailVerified) {
        showAlert(
          ta("emailVerifiedTitle") || "注册完成",
          ta("emailVerified") || "邮箱已确认，可以开始使用了！",
          tk("ok") || "我知道了",
          "success",
          async function () {
            try {
              if (auth && auth.currentUser) {
                await auth.signOut();
              }
            } catch (e) {}
            navigate("/login");
          }
        );
        return;
      }
    } catch (e) {}

    try {
      var base = window.location.origin;
      var langToUse = i18n && i18n.language ? i18n.language : "zh";
      var actionCodeSettings = {
        url: base + "/verify-email?lang=" + langToUse,
        handleCodeInApp: true,
      };
      await sendEmailVerification(user, actionCodeSettings);
      showAlert(
        tv("successTitle") || "提示",
        tv("success") || "验证邮件已发送，请注意收件箱和垃圾箱。",
        tk("ok") || "我知道了",
        "info"
      );
      setStatus("waiting");
    } catch (err) {
      console.error("resendVerification error:", err);
      showAlert(
        tv("errorTitle") || "提示",
        (tv("error") || "发送没有完成") + "：" + (err.message || ""),
        tk("ok") || "我知道了",
        "error"
      );
    }
  };

  const handleLogout = async function () {
    try {
      if (auth && auth.currentUser) {
        await auth.signOut();
      }
      showAlert(
        tv("successTitle") || "提示",
        tv("doneMessage") || "完成。正在跳转...",
        tk("ok") || "我知道了",
        "info",
        function () {
          navigate("/login");
        }
      );
    } catch (err) {
      showAlert(
        tv("errorTitle") || "提示",
        (tv("error") || "操作没有完成") + "：" + (err.message || ""),
        tk("ok") || "我知道了",
        "error",
        function () {
          navigate("/login");
        }
      );
    }
  };

  const handleVerifyAndChangeEmail = async function (oobCode) {
    setStatus("processing");
    setProcessingMsg(tv("redirecting") || "");

    try {
      await applyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }
      clearUrlParams();
      setStatus("success");
      showAlert(
        tv("changeEmailSuccessTitle") || "邮箱已更新",
        tv("changeEmailSuccess") || "您的邮箱更改已确认。请使用新邮箱登录。",
        tk("ok") || "我知道了",
        "success",
        async function () {
          try {
            if (auth && auth.currentUser) {
              await auth.signOut();
            }
          } catch (e) {}
          navigate("/login");
        }
      );
    } catch (error) {
      clearUrlParams();
      setStatus("error");
      showAlert(
        tv("errorTitle") || "提示",
        (tv("invalidLink") || "该链接无效或已过期。") +
          " " +
          (error.message || ""),
        tk("ok") || "我知道了",
        "error",
        function () {
          navigate("/login");
        }
      );
    }
  };

  const handleRecoverEmail = async function (oobCode) {
    setStatus("processing");
    setProcessingMsg(tv("redirecting") || "");

    try {
      var restoredEmail = "";
      try {
        var info = await checkActionCode(auth, oobCode);
        if (info && info.data) {
          if (info.data.email) {
            restoredEmail = info.data.email;
          } else if (info.data.fromEmail) {
            restoredEmail = info.data.fromEmail;
          } else if (info.data.previousEmail) {
            restoredEmail = info.data.previousEmail;
          }
        }
      } catch (e) {}

      await applyActionCode(auth, oobCode);
      if (auth.currentUser) {
        await auth.currentUser.reload();
      }

      if (restoredEmail) {
        try {
          var base = window.location.origin;
          await sendPasswordResetEmail(auth, restoredEmail, {
            url: base + "/reset-password",
          });
        } catch (e) {}
      }

      clearUrlParams();
      setStatus("success");
      showAlert(
        tv("recoverEmailSuccessTitle") || "邮箱已恢复",
        tv("recoverEmailSuccess") ||
          "您的邮箱已恢复。我们已向该邮箱发送重置密码邮件（如适用），请按邮件提示继续。",
        tk("ok") || "我知道了",
        "success",
        async function () {
          try {
            if (auth && auth.currentUser) {
              await auth.signOut();
            }
          } catch (e) {}
          navigate("/login");
        }
      );
    } catch (error) {
      clearUrlParams();
      setStatus("error");
      showAlert(
        tv("errorTitle") || "提示",
        (tv("invalidLink") || "该链接无效或已过期。") +
          " " +
          (error.message || ""),
        tk("ok") || "我知道了",
        "error",
        function () {
          navigate("/login");
        }
      );
    }
  };

  // ✅ 关键修复：在 VerifyEmail 页面统一语言，并写回 localStorage / i18next
  useEffect(
    function () {
      try {
        var finalLang = "zh";

        // 1）先看 URL 上有没有 ?lang=xx
        try {
          var currentUrl = new URL(window.location.href);
          var urlLang = currentUrl.searchParams.get("lang");
          if (urlLang === "en" || urlLang === "zh") {
            finalLang = urlLang;
          }
        } catch (eUrl) {}

        // 2）如果 URL 没有合法值，再看 selectedLang
        try {
          var saved = localStorage.getItem("selectedLang");
          if (saved === "en" || saved === "zh") {
            finalLang = saved;
          }
        } catch (eStore) {}

        // 3）应用到 i18n
        if (i18n && i18n.language !== finalLang) {
          i18n.changeLanguage(finalLang);
        }

        // 4）写回 localStorage，保证后续 /login 还是同一种语言
        try {
          localStorage.setItem("selectedLang", finalLang);
          // i18next 自己的缓存 key
          localStorage.setItem("i18nextLng", finalLang);
        } catch (eStore2) {}
      } catch (e) {
        console.warn("VerifyEmail: init lang failed", e);
      }
    },
    [i18n]
  );

  useEffect(
    function () {
      var oobCode = query.get("oobCode");
      var mode = query.get("mode");

      if (!oobCode) {
        setStatus("waiting");
        setProcessingMsg(tv("description") || "");
        return;
      }

      if (lastHandledOobRef.current === oobCode) {
        return;
      }
      lastHandledOobRef.current = oobCode;

      try {
        var key = "handled_oob_" + oobCode;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
        } else {
          return;
        }
      } catch (e) {}

      if (mode === "verifyEmail") {
        setStatus("processing");
        setProcessingMsg(tv("redirecting") || "");
        applyActionCode(auth, oobCode)
          .then(async function () {
            if (auth.currentUser) {
              await auth.currentUser.reload();
            }
            clearUrlParams();
            setStatus("success");
            showAlert(
              ta("emailVerifiedTitle") || "注册完成",
              ta("emailVerified") || "邮箱已确认，可以开始使用了！",
              tk("ok") || "我知道了",
              "success",
              async function () {
                try {
                  if (auth && auth.currentUser) {
                    await auth.signOut();
                  }
                } catch (e) {}
                navigate("/login");
              }
            );
          })
          .catch(function (error) {
            clearUrlParams();
            setStatus("error");
            var message =
              error && error.code === "auth/invalid-action-code"
                ? tv("invalidLink") ||
                  "该验证链接已使用或已过期，请重新发送验证邮件。"
                : (tv("error") || "验证遇到问题") +
                  "：" +
                  (error && error.message ? error.message : "");
            showAlert(
              tv("errorTitle") || "提示",
              message,
              tk("ok") || "我知道了",
              "error",
              function () {
                navigate("/login");
              }
            );
          });
      } else if (mode === "verifyAndChangeEmail") {
        handleVerifyAndChangeEmail(oobCode);
      } else if (mode === "recoverEmail") {
        handleRecoverEmail(oobCode);
      } else {
        clearUrlParams();
        setStatus("error");
        showAlert(
          tv("errorTitle") || "提示",
          tv("invalidLink") || "无效的验证链接。",
          tk("ok") || "我知道了",
          "error",
          function () {
            navigate("/login");
          }
        );
      }
    },
    [query, auth, navigate, showAlert, ta, tk, tv]
  );

  var buttonGroupStyle = {
    width: "min(320px, 90%)",
    margin: "18px auto 0",
    display: "block",
  };

  return (
    <div className="verify-email-page">
      <div className="auth-container">
        <h2>{tv("title") || "请确认您的邮箱"}</h2>
        {status === "processing" && (
          <p className="verify-msg">
            {processingMsg || tv("redirecting") || "正在处理，请稍候…"}
          </p>
        )}
        {status === "waiting" && (
          <>
            <p className="verify-msg">
              {tv("description") ||
                "我们已向您的邮箱发送了一封确认邮件，请点击邮件中的按钮完成确认。"}
            </p>
            <div style={buttonGroupStyle}>
              <button className="button" onClick={resendVerification}>
                {tv("resend") || "重新发送验证邮件"}
              </button>
              <button
                className="linkButton"
                style={{ marginTop: 12, display: "block" }}
                onClick={function () {
                  navigate("/login");
                }}
              >
                {tv("backToLogin") || ta("login") || "返回登录"}
              </button>
              <button
                className="linkButton"
                style={{ marginTop: 8, display: "block" }}
                onClick={handleLogout}
              >
                {tv("logout") || "退出登录"}
              </button>
            </div>
          </>
        )}
        {status === "success" && (
          <p className="verify-success">
            {tv("successMessage") || "邮箱已确认，可以开始使用了！"}
          </p>
        )}
        {status === "error" && (
          <>
            <p className="verify-error">
              {tv("errorMessage") || "链接不可用或已过期。"}
            </p>
            <div style={buttonGroupStyle}>
              <button className="button" onClick={resendVerification}>
                {tv("resend") || "重新发送验证邮件"}
              </button>
              <button
                className="linkButton"
                style={{ marginTop: 12, display: "block" }}
                onClick={function () {
                  navigate("/login");
                }}
              >
                {tv("backToLogin") || ta("login") || "返回登录"}
              </button>
              <button
                className="linkButton"
                style={{ marginTop: 8, display: "block" }}
                onClick={handleLogout}
              >
                {tv("logout") || "退出登录"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
